// locale-unextracted-ignore-file
import { FullWidthBox } from '@app/components/base/FullWidthBox';
import { usePlaybackVideoJsPlayer } from '@app/core/playback/video/useVideoPlaybackState';
import { getHlsFromVideoJsPlayer } from '@app/core/video-player/video-js/hls-playback-fix/hls-pdt-playback';
import { getQueryParam } from '@mi-front/core';
import { Box, Link, Typography } from '@mui/material';
import { useCallback, useRef } from 'react';
import { HlsTimelineStatsTable } from './HlsTimelineStatsTable';
import { useHlsTimelineChart } from './useHlsTimelineChart';
import { useHlsTimelineStats } from './useHlsTimelineStats';

/**
 * Debug-only HLS timeline chart (hls.js demo parity).
 * Enable with ?hls_debug=1 on the Logger stream page.
 */
export function HlsTimelineChart() {
  const enabled = getQueryParam('hls_debug');
  const player = usePlaybackVideoJsPlayer();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activePlayer = enabled ? player : null;
  const chartRef = useHlsTimelineChart(canvasRef, containerRef, activePlayer);
  const stats = useHlsTimelineStats(activePlayer);

  const handleResetZoom = useCallback(() => {
    chartRef.current?.resetZoom();
  }, [chartRef]);

  if (!enabled) {
    return null;
  }

  if (!player) {
    return null;
  }

  const hls = getHlsFromVideoJsPlayer(player);
  if (!hls) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Timeline unavailable: player does not use hls.js.
      </Typography>
    );
  }

  return (
    <FullWidthBox sx={{ mt: 1, mb: 4, p: 2 }}>
      <Box ref={containerRef} sx={{ width: '100%', overflow: 'auto' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          <strong>HLS Timeline — double-click: zoom, drag: pan, click: seek </strong>
          <Link component="button" variant="caption" onClick={handleResetZoom}>
            Reset zoom
          </Link>
        </Typography>
        <Box sx={{ width: '100%', minHeight: 120 }}>
          <canvas ref={canvasRef} style={{ width: '100%', touchAction: 'manipulation' }} />
        </Box>
        <HlsTimelineStatsTable stats={stats} />
      </Box>
    </FullWidthBox>
  );
}
